import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertCircle } from "lucide-react";

import { AuthPanel, AuthScreen, Button } from "@h2j-data-team/ui";

import { BrandLogo } from "@/components/brand-logo";
import { PRODUCT_NAME } from "@repo/shared";

export const Route = createFileRoute("/verify-email/confirm")({
  validateSearch: (search: Record<string, unknown>) => ({
    error: typeof search.error === "string" ? search.error : undefined,
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  head: () => ({ meta: [{ title: `Email verified — ${PRODUCT_NAME}` }] }),
  component: VerifyEmailConfirmPage,
});

function VerifyEmailConfirmPage() {
  const { error, redirect } = Route.useSearch();
  const { t } = useTranslation("auth");
  const isInvalid = !!error;

  return (
    <AuthScreen logo={<BrandLogo className="h-8 w-auto" />}>
      {isInvalid ? (
        <AuthPanel
          tone="danger"
          icon={<AlertCircle />}
          title={t("verifyEmailConfirm.invalid.heading")}
          description={t("verifyEmailConfirm.invalid.description")}
          actions={
            <>
              <Button size="lg" variant="outline" asChild>
                <Link to="/verify-email" search={{ email: undefined }}>
                  {t("verifyEmail.resend")}
                </Link>
              </Button>
              <Button size="lg" variant="ghost" asChild>
                <Link
                  to="/login"
                  search={{
                    mode: undefined,
                    token: undefined,
                    error: undefined,
                    redirect: undefined,
                    email: undefined,
                  }}
                >
                  {t("backToSignIn")}
                </Link>
              </Button>
            </>
          }
        />
      ) : (
        <AuthPanel
          tone="success"
          icon={<CheckCircle2 />}
          title={t("verifyEmailConfirm.success.heading")}
          description={t("verifyEmailConfirm.success.description", { productName: PRODUCT_NAME })}
          actions={
            <Button size="lg" asChild>
              <Link
                to="/login"
                search={{
                  mode: undefined,
                  token: undefined,
                  error: undefined,
                  redirect: redirect,
                  email: undefined,
                }}
              >
                {t("verifyEmailConfirm.success.continue")}
              </Link>
            </Button>
          }
        />
      )}
    </AuthScreen>
  );
}
